import {
  captureSentryError,
  type RequestBody,
  showApiErrorNotification,
  useApiMutation,
  useInvalidateApiQueries,
} from '@vrplatform/data';
import { showWarnNotification } from '@vrplatform/theme';
import { bankersRound, day, type Maybe } from '@vrplatform/utils';
import { useCallback } from 'react';
import { buildReservationAdjustmentEditMutationBody } from './useReservationAdjustmentMutation.utils';

export type ReservationFeeAddInputs = {
  description: string;
  centTotal: number | undefined;
  recurringFeeId: string | null;
  postingAt: Maybe<string>;
};

export function useReservationFeeMutation(
  reservationId: string | null,
  onSuccess: () => void
) {
  const invalidate = useInvalidateApiQueries();

  const { mutateAsync: deleteAdjustment, isPending: loadingDeleteAdjustment } =
    useApiMutation('delete', '/reservations/{reservationId}/adjustments/{id}');

  const { mutateAsync: createAdjustment, isPending: loadingCreateAdjustment } =
    useApiMutation('post', '/reservations/{reservationId}/adjustments');
  const { mutateAsync: updateAdjustment, isPending: loadingUpdateAdjustment } =
    useApiMutation('put', '/reservations/{reservationId}/adjustments/{id}');

  const submit = useCallback(
    async (
      args:
        | { data: ReservationFeeAddInputs; type: 'add' }
        | {
            data: {
              id: string;
            };
            type: 'remove';
          }
        | {
            data: ReservationFeeAddInputs;
            lineId: string;
            type: 'edit';
          }
    ) => {
      if (!reservationId) {
        captureSentryError('Missing reservationId');
        return showWarnNotification({
          title: 'Missing reservation',
          message: 'Please reach out to support if the issue persists.',
        });
      }

      try {
        if (args.type === 'edit') {
          await updateAdjustment({
            params: {
              path: {
                reservationId,
                id: args.lineId,
              },
            },
            body: buildReservationAdjustmentEditMutationBody({
              description: args.data.description,
              centTotal: args.data.centTotal,
              postingAt: args.data.postingAt,
            }),
          });

          invalidate(['reservations']);
          onSuccess();
          return;
        }

        const formatInputs = (input: ReservationFeeAddInputs) =>
          ({
            description: input.description.trim() || null,
            amount: bankersRound(input.centTotal ?? 0),
            recurringFeeId: input.recurringFeeId,
            party: 'manager',
            generalLedgerPostingAt: input.postingAt
              ? day(input.postingAt).yyyymmdd()
              : undefined,
          }) satisfies RequestBody<'post:/reservations/{reservationId}/adjustments'>;

        const _create =
          args.type === 'add'
            ? Array.isArray(args.data)
              ? args.data.map(formatInputs)
              : [formatInputs(args.data)]
            : undefined;

        const _delete = args.type === 'remove' ? [args.data.id] : undefined;

        // Delete adjustments
        if (_delete?.length) {
          await Promise.all(
            _delete.map(async (id) => {
              await deleteAdjustment({
                params: {
                  path: { reservationId, id },
                },
              });
            })
          );
        }
        // Add adjustments
        if (_create?.length) {
          await Promise.all(
            _create.map(async (input) => {
              await createAdjustment({
                params: {
                  path: { reservationId },
                },
                body: input,
              });
            })
          );
        }
        /*await mutateAsync({
          params: {
            path: {
              id: reservationId,
            },
          },
          body: {
            adjustments_create:
              args.type === 'add'
                ? [
                    {
                      description: args.data.description.trim() || null,
                      amount: bankersRound(args.data.centTotal ?? 0),
                      recurringFeeId: args.data.recurringFeeId,
                      party: 'manager',
                      generalLedgerPostingAt: args.data.postingAt
                        ? day(args.data.postingAt).yyyymmdd()
                        : undefined,
                    },
                  ]
                : undefined,
            adjustments_delete:
              args.type === 'remove' ? [args.data.id] : undefined,
          },
        });*/

        invalidate(['reservations']);
        onSuccess();
      } catch (error: any) {
        showApiErrorNotification({
          title: 'Failed to update reservation',
          defaultMessage:
            'We failed to update the reservation. Please try again later and if the problem persists, contact support.',
          error,
        });
      }
    },
    [
      reservationId,
      onSuccess,
      deleteAdjustment,
      createAdjustment,
      updateAdjustment,
      invalidate,
    ]
  );

  return {
    loading:
      loadingDeleteAdjustment ||
      loadingCreateAdjustment ||
      loadingUpdateAdjustment,
    submit,
  };
}
